import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaEye, FaMousePointer } from "react-icons/fa";

interface PreviewOverlayProps {
  isActive: boolean;
  tabName: string;
  tabColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}

/**
 * Preview overlay component that wraps content and shows visual indicators
 * when a tab is being previewed on hover.
 */
const PreviewOverlay: React.FC<PreviewOverlayProps> = ({
  isActive,
  tabName,
  tabColor,
  onConfirm,
  onCancel,
  children,
}) => {
  if (!isActive) {
    return <div className="h-full w-full">{children}</div>;
  }

  return (
    <div className="relative w-full h-full">
      {/* Preview content with dimmed overlay */}
      <div className="relative">
        {children}

        {/* Semi-transparent overlay with gradient */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-300"
          style={{
            background: `linear-gradient(135deg, ${tabColor}15 0%, transparent 50%)`,
          }}
        />

        {/* Border indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 pointer-events-none border-4 rounded-lg"
          style={{ borderColor: `${tabColor}50` }}
        />
      </div>

      {/* Preview badge (top) */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg z-50"
          style={{ backgroundColor: tabColor }}
        >
          <FaEye className="text-white" size={12} />
          <span className="text-white text-sm font-medium">
            PREVIEW: {tabName}
          </span>
        </motion.div>
      </AnimatePresence>

      {/* Help bar (bottom) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2, delay: 0.1 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 px-4 py-2 bg-surface-elevated/90 backdrop-blur-sm rounded-full text-content-primary text-sm shadow-arc-lg border border-arc-border z-50"
      >
        <button
          onClick={onConfirm}
          className="flex items-center gap-2 hover:text-accent-light transition-colors"
        >
          <FaMousePointer size={12} />
          <span>Click to switch</span>
        </button>
        <span className="text-content-tertiary">•</span>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 hover:text-content-tertiary transition-colors"
        >
          <span className="px-1.5 py-0.5 bg-content-secondary rounded text-xs font-mono">
            ESC
          </span>
          <span>to cancel</span>
        </button>
      </motion.div>
    </div>
  );
};

export default PreviewOverlay;
